"use client";

import { Play, ExternalLink, Users } from "lucide-react";
import { useTranslations } from "next-intl";
import { useState } from "react";

import { Button } from "@/shared/ui/buttons/button";
import { Badge } from "@/shared/ui/data-display/badge";

import { useTwitchUser, useTwitchStream } from "../api/use-twitch";

interface TwitchStreamPreviewProps {
  channelName: string;
  onPlay?: () => void;
  onError?: (error: string) => void;
}

export function TwitchStreamPreview({
  channelName,
  onPlay,
}: TwitchStreamPreviewProps) {
  const tUi = useTranslations("Ui");
  const [isPlaying, setIsPlaying] = useState(false);

  // Получаем данные пользователя и стрима
  const {
    user: userData,
    loading: userLoading,
    error: userError,
  } = useTwitchUser(channelName);
  const {
    stream: streamData,
    loading: streamLoading,
    error: streamError,
  } = useTwitchStream(channelName);

  const handlePlay = () => {
    setIsPlaying(true);
    onPlay?.();
  };

  // const handleError = () => {
  //   setIsPlaying(false)
  //   onError?.('Не удалось загрузить стрим')
  // }

  // Если стрим онлайн, показываем превью
  if (streamData && !isPlaying) {
    return (
      <div
        role="button"
        tabIndex={0}
        className="w-full h-full relative group cursor-pointer"
        onClick={handlePlay}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handlePlay();
          }
        }}
      >
        {/* Превью стрима */}
        <div
          className="w-full h-full bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url(${streamData.thumbnail_url.replace("{width}", "1280").replace("{height}", "720")})`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

          {/* Информация о стриме */}
          <div className="absolute bottom-4 left-4 right-4 text-white">
            <div className="flex items-center gap-2 mb-2">
              <Badge className="bg-red-600 hover:bg-red-700">
                <div className="w-2 h-2 bg-white rounded-full mr-1" />
                LIVE
              </Badge>
              <Badge variant="secondary" className="bg-black/50 text-white">
                <Users className="h-3 w-3 mr-1" />
                {streamData.viewer_count.toLocaleString()}
              </Badge>
            </div>
            <h3 className="font-semibold text-lg mb-1 line-clamp-2">
              {streamData.title}
            </h3>
            <p className="text-sm text-gray-300">{streamData.game_name}</p>
          </div>

          {/* Кнопка воспроизведения */}
          <div className="absolute inset-0 flex items-center justify-center">
            <Button
              variant="outline"
              size="lg"
              className="bg-transparent border-white/30 text-white hover:bg-white/10 hover:border-white/50 transition-all opacity-0 group-hover:opacity-100"
            >
              <Play className="h-6 w-6 mr-2" />
              {tUi("twitchStreamPreview.smotretLive")}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Если стрим офлайн, показываем информацию о канале
  if (!streamData && !streamLoading && userData) {
    return (
      <div className="w-full h-full bg-gray-900 flex items-center justify-center p-6">
        <div className="text-center text-white max-w-md">
          <div className="mb-6">
            <div className="w-16 h-16 mx-auto mb-4 bg-gray-600 rounded-full flex items-center justify-center">
              <Play className="h-8 w-8" />
            </div>
            <h3 className="text-lg font-semibold mb-2">{tUi("twitchStreamPreview.kanalOflayn")}</h3>
            <div className="text-sm text-gray-300 space-y-2 mb-6">
              <p>{userData.display_name} сейчас не стримит</p>
              <p className="text-xs text-gray-400">
                {tUi("twitchStreamPreview.podpishitesNaKanalChtoby")}
              </p>
            </div>
          </div>
          <div className="space-y-3">
            <Button
              asChild
              variant="outline"
              className="bg-transparent border-white/30 text-white hover:bg-white/10 hover:border-white/50 w-full"
            >
              <a
                href={`https://www.twitch.tv/${channelName}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                {tUi("twitchStreamPreview.otkrytKanal")}
              </a>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Загрузка
  if (userLoading || streamLoading) {
    return (
      <div className="w-full h-full bg-gray-900 flex items-center justify-center">
        <div className="text-center text-white">
          <div className="animate-spin w-8 h-8 border-2 border-white border-t-transparent rounded-full mx-auto mb-2"></div>
          <p className="text-sm">{tUi("twitchStreamPreview.zagruzka")}</p>
        </div>
      </div>
    );
  }

  // Ошибка
  if (userError || streamError) {
    return (
      <div className="w-full h-full bg-gray-900 flex items-center justify-center p-6">
        <div className="text-center text-white max-w-md">
          <div className="mb-6">
            <div className="w-16 h-16 mx-auto mb-4 bg-red-600 rounded-full flex items-center justify-center">
              <Play className="h-8 w-8" />
            </div>
            <h3 className="text-lg font-semibold mb-2">{tUi("twitchStreamPreview.oshibkaZagruzki")}</h3>
            <div className="text-sm text-gray-300 space-y-2 mb-6">
              <p>{tUi("twitchStreamPreview.neUdalosZagruzitDannye")}</p>
              <p className="text-xs text-gray-400">
                {tUi("twitchStreamPreview.provertePravilnostImeniKanala")}
              </p>
            </div>
          </div>
          <div className="space-y-3">
            <Button
              asChild
              variant="outline"
              className="bg-transparent border-white/30 text-white hover:bg-white/10 hover:border-white/50 w-full"
            >
              <a
                href={`https://www.twitch.tv/${channelName}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                {tUi("twitchStreamPreview.otkrytKanal")}
              </a>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Fallback
  return (
    <div className="w-full h-full bg-gray-900 flex items-center justify-center">
      <div className="text-center text-white">
        <Play className="h-12 w-12 mx-auto mb-4 text-gray-400" />
        <p className="text-sm text-gray-400">{tUi("twitchStreamPreview.kanalNeNayden")}</p>
      </div>
    </div>
  );
}
